import React from 'react';
import JsonLd from '../../components/JsonLd';
import SEOHead from '../../components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import {
  Download, Package, CheckCircle2, GitMerge, Monitor, RefreshCw, Bell, ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const SoftwareProvisioningPage = () => {
  const benefits = [
    {
      icon: Package,
      title: 'Automated license validation and catalog policy enforcement',
      description:
        'Every request is checked against the approved software catalog and available license pool before any deployment or approval workflow is triggered - preventing unauthorized installs.',
    },
    {
      icon: GitMerge,
      title: 'Policy and approval orchestration for exceptions',
      description:
        'Requests for non-catalog software are automatically routed through manager, budget, and security approval chains - with escalation and SLA tracking built in.',
    },
    {
      icon: Monitor,
      title: 'Endpoint deployment automation with failure handling',
      description:
        'Approved installs are pushed via Intune, SCCM, or JAMF with automated retry logic, failure detection, and user notification when deployment completes or encounters an issue.',
    },
  ];

  const capabilities = [
    'Integrate ITSM + endpoint tools (Intune/SCCM/JAMF) + license systems + app catalog',
    'Define catalog policies: approved vs exception software, versions, prerequisites',
    'Configure approvals: manager, budget, and security rules',
    'Configure flows: install request, access provisioning, troubleshooting, and retries',
    'Pilot top catalog items → expand across software portfolio',
  ];

  const features = [
    {
      icon: Package,
      title: 'App Catalog Management',
      desc: 'Maintain an approved software catalog with version control, prerequisites, and platform requirements - the source of truth for every deployment request.',
      gradient: 'from-indigo-500 to-violet-500',
    },
    {
      icon: CheckCircle2,
      title: 'License Validation',
      desc: 'Check available license inventory before approving any install - preventing over-deployment, license violations, and unnecessary procurement.',
      gradient: 'from-violet-500 to-purple-500',
    },
    {
      icon: GitMerge,
      title: 'Approval Orchestration',
      desc: 'Route exception requests through configured approval chains - manager, security, or budget owner - with escalation rules and SLA monitoring.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: Monitor,
      title: 'Intune / SCCM / JAMF Deployment',
      desc: 'Trigger deployment to Windows, macOS, and mobile endpoints via the configured endpoint management tool - with deployment status tracking and reporting.',
      gradient: 'from-pink-500 to-rose-500',
    },
    {
      icon: RefreshCw,
      title: 'Failure Handling & Retries',
      desc: 'Automatically detect deployment failures, retry based on configured policy, and notify the user and ITSM team when issues cannot be resolved automatically.',
      gradient: 'from-rose-500 to-red-400',
    },
    {
      icon: Bell,
      title: 'ITSM Notification',
      desc: 'Every stage - request received, approved, deployed, completed - generates an ITSM update and a notification to the requesting user.',
      gradient: 'from-orange-400 to-amber-400',
    },
  ];

  const steps = [
    {
      step: '01',
      title: 'User requests install or app access',
      desc: 'The user submits a software request via voice, chat, or the ITSM portal - specifying the application needed and any justification for non-catalog items.',
    },
    {
      step: '02',
      title: 'AI captures device ID, justification, required timeline',
      desc: 'The agent collects the necessary details: target device or user, business justification, desired deployment window, and version requirements.',
    },
    {
      step: '03',
      title: 'AI checks catalog and license availability',
      desc: 'The request is evaluated against the approved software catalog. License pool availability is checked to confirm the install can proceed without procurement.',
    },
    {
      step: '04',
      title: 'AI routes approvals if required',
      desc: 'Non-catalog or high-cost requests are routed through the configured approval chain - with automated reminders and escalation if approvers do not respond within SLA.',
    },
    {
      step: '05',
      title: 'AI triggers deployment or access provisioning',
      desc: 'Once approved, the deployment is triggered via Intune, SCCM, or JAMF. For SaaS access, the provisioning API call is executed directly.',
    },
    {
      step: '06',
      title: 'AI updates ITSM and notifies user',
      desc: 'The ITSM ticket is updated to reflect completion and the user receives a notification confirming the install or access is ready.',
    },
  ];

  const stats = [
    { value: '~45%', label: 'Ticket deflection' },
    { value: '~40%', label: 'L1 workload reduction' },
    { value: '<$5', label: 'Cost per ticket (vs $25–$40)' },
    { value: '24/7', label: 'Request intake and processing' },
  ];

  const faqs = [
    {
      q: 'Does it support both macOS and Windows?',
      a: 'Yes. Digian.ai integrates with Intune for Windows and macOS, JAMF for Apple device management, and SCCM for Windows environments. The correct deployment tool is selected automatically based on the target device platform.',
    },
    {
      q: 'What happens with non-standard or non-catalog software?',
      a: 'Non-catalog requests are captured with full justification details and routed through the configured exception approval workflow. Security review, manager approval, and budget owner sign-off can all be included in the chain. Once approved, deployment follows the same automated path as catalog items.',
    },
    {
      q: 'Can it handle concurrent deployments to multiple devices?',
      a: 'Yes. The system can trigger batch deployments to multiple endpoints simultaneously - for example, rolling out a new application to an entire team - via the endpoint management tool, with per-device status tracking and failure handling.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="useCaseSoftwareProvisioning" />
      <JsonLd schema={{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Home","item":"https://digian.ai"},{"@type":"ListItem","position":2,"name":"Use Cases","item":"https://digian.ai/use-cases"},{"@type":"ListItem","position":3,"name":"Software Provisioning","item":"https://digian.ai/use-cases/software-provisioning"}]}} />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-sm font-medium tracking-wide text-indigo-600 uppercase">IT Helpdesk / Software Installation &amp; Provisioning</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900">
              Automated Software Install &amp; App Access - Catalog + Approvals + Deployment
            </h1>
            <p className="text-lg text-slate-600">
              AI handles software installs and app access requests using approved catalog policies, license checks, approvals, and automated deployment via endpoint tools including Intune, SCCM, and JAMF.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/demo"><Button size="lg">Request a Demo</Button></Link>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              {['Catalog + license validation', 'Intune / SCCM / JAMF', 'Policy-driven approvals'].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />{t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Download className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-indigo-600 uppercase">Software Provisioning Automation</p>
                <p className="text-sm text-slate-600">Catalog · Approvals · Deployment · ITSM</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-slate-700">
              {capabilities.map((item, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <Download className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-indigo-600 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <p className="text-3xl font-extrabold text-white">{s.value}</p>
                <p className="text-indigo-200 text-sm mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Why IT teams automate software provisioning</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div key={b.title} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Capabilities</p>
            <h2 className="text-3xl font-bold text-slate-900">Everything needed to automate software deployment</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all group">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-4`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Process</p>
            <h2 className="text-3xl font-bold text-slate-900">How software provisioning automation works at runtime</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={s.step} {...fadeUp} transition={{ delay: i * 0.12 }}
                className="bg-white rounded-2xl border border-slate-200 p-8">
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-5xl font-black text-indigo-100">{s.step}</span>
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Download className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">From request to deployed - without an L1 ticket</h2>
            <p className="text-xl text-indigo-100">
              Catalog validation, approvals, endpoint deployment, and ITSM closure - all automated, 24/7.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo"><Button size="lg" variant="secondary">Request Demo</Button></Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Talk to Our Team <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <p className="text-indigo-200 text-sm">Intune · SCCM · JAMF · License-validated · Policy-driven</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SoftwareProvisioningPage;
